/**
 * components/team/BranchAccessField.js
 *
 * "Branch access" for one team member (4 Oct 2026): the whole business, or
 * only some branches. Izi's rule: a member with no branch assigned works
 * across the whole business; a member given branches sees and runs only
 * those (orders, availability, prices, hours and sales).
 *
 *   <BranchAccessField value={scope} onChange={setScope} locations={locations} />
 *
 * `value` is the stored shape { mode: "ALL"|"BRANCHES", outletIds, includeMain };
 * `locations` come from GET /api/vendor-team/:owner/locations. The vendor's
 * main location is `includeMain`, never an outlet id. A branch that has been
 * deleted stays in a scope until someone unticks it, and shows as "Deleted
 * branch" so it can be.
 *
 * Used by the add dialog and the Branches dialog on the team page, so both
 * read the same.
 */
import React from "react";
import { MAIN, branchScopeError, normalizeBranchScope } from "../../utilities/vendorScope.js";
import "../branch/branch.css";

export const BRANCH_ACCESS_HINT =
  "They see and run only these branches: orders, availability, prices, hours and sales.";

const label = (l) => {
  const extras = [];
  if (l.isMain) extras.push("main location");
  if (l.isActive === false) extras.push("closed");
  return extras.length ? `${l.name} (${extras.join(", ")})` : l.name;
};

export default function BranchAccessField({ id = "branch-access", value, onChange, locations = [], disabled = false, showError = false }) {
  const scope = normalizeBranchScope(value);
  const branches = scope.mode === "BRANCHES";
  const known = new Set((locations || []).map((l) => String(l.id)));
  // Ids still in the scope whose branch was deleted: listed so they can go.
  const deleted = scope.outletIds.filter((outletId) => !known.has(outletId));
  const error = showError ? branchScopeError(scope) : null;

  const set = (patch) => onChange && onChange({ ...scope, ...patch });
  const isChecked = (locationId) => (locationId === MAIN ? scope.includeMain : scope.outletIds.includes(locationId));
  const toggle = (locationId) => {
    if (locationId === MAIN) return set({ includeMain: !scope.includeMain });
    const has = scope.outletIds.includes(locationId);
    return set({ outletIds: has ? scope.outletIds.filter((x) => x !== locationId) : [...scope.outletIds, locationId] });
  };

  return (
    <div className="lk-field" role="radiogroup" aria-labelledby={`${id}-label`}>
      <span className="lk-field__label" id={`${id}-label`}>Branch access</span>
      <label className={`lk-branch-choice${!branches ? " is-selected" : ""}`}>
        <input
          type="radio"
          name={`${id}-mode`}
          checked={!branches}
          disabled={disabled}
          onChange={() => set({ mode: "ALL" })}
        />
        <span>Whole business (head office)</span>
      </label>
      <label className={`lk-branch-choice${branches ? " is-selected" : ""}`}>
        <input
          type="radio"
          name={`${id}-mode`}
          checked={branches}
          disabled={disabled}
          onChange={() => set({ mode: "BRANCHES" })}
        />
        <span>Only these branches</span>
      </label>
      {branches && (
        <div className="lk-branch-list" data-testid={`${id}-list`}>
          {(locations || []).map((l) => (
            <label key={l.id}>
              <input
                type="checkbox"
                checked={isChecked(String(l.id))}
                disabled={disabled}
                onChange={() => toggle(String(l.id))}
              />
              <span>{label(l)}</span>
            </label>
          ))}
          {deleted.map((outletId) => (
            <label key={outletId}>
              <input type="checkbox" checked disabled={disabled} onChange={() => toggle(outletId)} />
              <span>Deleted branch</span>
            </label>
          ))}
        </div>
      )}
      {error ? <span className="lk-field__error">{error}</span> : <span className="lk-field__hint">{BRANCH_ACCESS_HINT}</span>}
    </div>
  );
}
